'use client';

import Link from 'next/link';
import { Github, Twitter, Linkedin, Mail, ArrowUpRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PublicFooterProps {
  locale: string;
}

export function PublicFooter({ locale = 'en' }: PublicFooterProps) {
  const footerLinks = {
    product: [
      { name: 'Features', href: `/${locale}/features` },
      { name: 'Pricing', href: `/${locale}/pricing` },
      { name: 'Security', href: `/${locale}/security` },
      { name: 'Roadmap', href: `/${locale}/resources` },
    ],
    company: [
      { name: 'About', href: `/${locale}/about` },
      { name: 'Team', href: `/${locale}/about/team` },
      { name: 'Careers', href: `/${locale}/about/careers` },
      { name: 'Contact', href: `/${locale}/contact` },
    ],
    resources: [
      { name: 'Blog', href: `/${locale}/resources/blog` },
      { name: 'Guides', href: `/${locale}/resources/guides` },
      // { name: 'Case Studies', href: `/${locale}/resources/case-studies` }, // Hidden - not ready for public
      { name: 'Documentation', href: `/${locale}/resources/documentation` },
    ],
    legal: [
      { name: 'Privacy Policy', href: `/${locale}/legal/privacy` },
      { name: 'Terms of Service', href: `/${locale}/legal/terms` },
      { name: 'SLA', href: `/${locale}/legal/sla` },
      { name: 'Cookie Policy', href: `/${locale}/legal/cookies` },
    ],
  };

  const socialLinks = [
    { name: 'GitHub', icon: Github, href: 'https://github.com/next-pal' },
    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/nextpal' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/next-pal' },
    { name: 'Email', icon: Mail, href: 'mailto:friends@next-pal.com' },
  ];

  return (
    <footer className="relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-black">
      {/* Gradient orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 dark:opacity-5" />
      
      <div className="relative container mx-auto px-6">
        {/* CTA Section */}
        <div className="py-20 border-b border-gray-200 dark:border-gray-800">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              Ready to get started?
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
              Start protecting your data today
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-10 max-w-2xl mx-auto">
              Join thousands of organizations that trust Next PAL Nexus for their backup infrastructure.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button size="lg" className="h-14 px-10 text-lg font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 hover:from-blue-700 hover:via-purple-700 hover:to-green-700 text-white shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105" asChild>
                <Link href="/signup">Start Free Trial →</Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-10 text-lg font-bold border-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300" asChild>
                <Link href="/contact">Talk to Sales</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Links Section */}
        <div className="py-16">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-12">
            {/* Logo and Tagline */}
            <div className="col-span-2">
              <Link href={`/${locale}`} className="group inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 via-purple-600 to-green-600 rounded-2xl flex items-center justify-center font-black text-white text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                  N
                </div>
                <div className="text-2xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
                  Next PAL
                </div>
              </Link>
              <p className="text-base text-gray-600 dark:text-gray-400 mb-8 max-w-sm leading-relaxed">
                Enterprise PostgreSQL backup management platform trusted by organizations worldwide.
              </p>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 hover:text-white transition-all duration-300 hover:scale-110 hover:shadow-lg"
                    aria-label={social.name}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="font-black text-gray-900 dark:text-white mb-6 text-sm uppercase tracking-wider">Product</h3>
              <ul className="space-y-3">
                {footerLinks.product.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="group inline-flex items-center gap-1 text-base text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="font-black text-gray-900 dark:text-white mb-6 text-sm uppercase tracking-wider">Company</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="group inline-flex items-center gap-1 text-base text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources Links */}
            <div>
              <h3 className="font-black text-gray-900 dark:text-white mb-6 text-sm uppercase tracking-wider">Resources</h3>
              <ul className="space-y-3">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="group inline-flex items-center gap-1 text-base text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-black text-gray-900 dark:text-white mb-6 text-sm uppercase tracking-wider">Legal</h3>
              <ul className="space-y-3">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <Link
                      href={link.href}
                      className="group inline-flex items-center gap-1 text-base text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {link.name}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-8 border-t border-gray-200 dark:border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} Next PAL Technologies, Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/20 rounded-full">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-semibold text-green-700 dark:text-green-400">All Systems Operational</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-500">
                <span className="font-semibold">SOC 2</span>
                <span>•</span>
                <span className="font-semibold">GDPR</span>
                <span>•</span>
                <span className="font-semibold">99.9% Uptime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
